import makeReducerFor from "./_genericDbReducer";
import { createSelector } from "reselect";
import { getFlowExportsForSelectedFlow, getFlowExportsArray } from "./flowExports";
import type { FlowExportPubmaticTemplateFields, FlowErrorsByItemId, FlowExport } from "../types/flowTypes";
import { DeliveryType } from "../enums/company";

const myGenericReducer = makeReducerFor("FLOW_EXPORT_PUBMATIC_TEMPLATE_FIELDS", "FlowExportTemplateId");
import subItemReducer from "./_genericFlowSubItemReducer";
import { DeploySettings } from "../types/stores/companyTable";

const myReducer = (state = {}, action) => subItemReducer(myGenericReducer(state, action), action);
export default myReducer;

///////// SELECTORS /////////////

export const getFlowExportPubmaticTemplateFieldsArray = createSelector(
    state => state.flowExportPubmaticTemplateFields.byId,
    (flowExportPubmaticTemplateFieldsById: {|
    [number]: FlowExportPubmaticTemplateFields,
    |}): Array < FlowExportPubmaticTemplateFields > => {
    const r: Array<FlowExportPubmaticTemplateFields> = Object.values(flowExportPubmaticTemplateFieldsById);
    return r;
}
);

export type FlowExportPubmaticTemplateFieldsByItemId = {
    [number]: Array<FlowExportPubmaticTemplateFields>,
};

export const getFlowExportPubmaticTemplateFieldsByFlowItemId = createSelector(
    state => getFlowExportPubmaticTemplateFieldsArray(state),
    (
        flowExportPubmaticTemplateFields: Array<FlowExportPubmaticTemplateFields>
    ): FlowExportPubmaticTemplateFieldsByItemId =>
        flowExportPubmaticTemplateFields.reduce((acc, row) => {
            if (acc[row.FlowItemId] == null) {
                acc[row.FlowItemId] = [];
            }
            acc[row.FlowItemId].push(row);
            return acc;
        }, {})
);

export const getFlowExportPubmaticTemplateFieldsForSelectedFlow = createSelector(
    state => getFlowExportsForSelectedFlow(state),
    state => getFlowExportPubmaticTemplateFieldsByFlowItemId(state),
    state => state.vars.destinations,
    (
        flowExports: Array<FlowExport>,
        flowExportPubmaticTemplateFieldsByItemId: FlowExportPubmaticTemplateFieldsByItemId,
        destinations: Array<any>
    ): Array<FlowExportPubmaticTemplateFields> => {
        let result = [];

        flowExports = flowExports.filter(x => {
            const thisDestination = destinations.find(y => y.PartnerAccessId == x.DestinationId);
            if (thisDestination && thisDestination.DeploySetting == DeploySettings.DeployPubmaticTemplate) {
                return x;
            }
        });

        const itemIds = flowExports.map(fi => fi.FlowItemId);
        for (const itemId of itemIds) {
            const templateFields = flowExportPubmaticTemplateFieldsByItemId[itemId];
            if (templateFields != null) {
                result = result.concat(templateFields);
            }
        }
        return result;
    }
);

const templateFieldsToErrorsById = (
    flowExports: Array<FlowExport>,
    flowExportPubmaticTemplateFields: Array<FlowExportPubmaticTemplateFields>,
    destinations: Array<any>,
    tapadDeliveryTypes: Array<any>
): FlowErrorsByItemId => {
    const errorsById = {};
    for (const flowExport of flowExports) {
        errorsById[flowExport.FlowItemId] = validateExportPubmaticTemplateFields(
            flowExport,
            flowExportPubmaticTemplateFields,
            destinations,
            tapadDeliveryTypes
        );
    }
    return errorsById;
};

export const getExportPubmaticTemplateErrorsForSelectedFlow = createSelector(
    state => getFlowExportsForSelectedFlow(state),
    state => getFlowExportPubmaticTemplateFieldsForSelectedFlow(state),
    state => state.vars.destinations,
    state => state.vars.tapadDeliveryTypes,
    templateFieldsToErrorsById
);

export const getExportPubmaticTemplateErrorsForAllFlows = createSelector(
    state => getFlowExportsArray(state),
    state => getFlowExportPubmaticTemplateFieldsArray(state),
    state => state.vars.destinations,
    templateFieldsToErrorsById
);

export const validateExportPubmaticTemplateFields = (
    flowExport: FlowExport,
    flowExportPubmaticTemplateFields: Array<FlowExportPubmaticTemplateFields>,
    destinations: Array<any>,
    tapadDeliveryTypes: Array<any>
) => {
    const errors = [];

    if (!destinations || (destinations && destinations.length == 0)) {
        return errors;
    }

    const ExportPubmaticTemplateFields = flowExportPubmaticTemplateFields.filter(
        x => x.FlowItemId == flowExport.FlowItemId
    );
    const destination = destinations.find(x => x.PartnerAccessId == flowExport.DestinationId);

    // No destination selected
    if (!destination || !destination.DeploySetting == DeploySettings.DeployPubmaticTemplate) {
        return errors;
    }

    // Delivery types dropdown empty
    if (!tapadDeliveryTypes || tapadDeliveryTypes.length == 0) {
        return errors;
    }

    // Missing data
    if (
        ExportPubmaticTemplateFields.length == 0 &&
        destination.DeploySetting == DeploySettings.DeployPubmaticTemplate
    ) {
        errors.push("Pubmatic Driver File Fields missing.");
        return errors;
    }

    const thisDeliveryType = tapadDeliveryTypes.find(x => x.TapadDeliveryTypeId == flowExport.DeliveryTypeId);
    if (!thisDeliveryType) {
        return errors;
    }

    const deliveryType = thisDeliveryType.DeliveryType;

    // Validations
    const noAudienceName = ExportPubmaticTemplateFields.filter(x => x.AudienceName == null || x.AudienceName == "");
    const audienceNameLength = ExportPubmaticTemplateFields.filter(
        x => x.AudienceName != null && x.AudienceName.length > 100
    );
    const noDescription = ExportPubmaticTemplateFields.filter(x => x.Description == null || x.Description == "");
    const noExpansionType = ExportPubmaticTemplateFields.filter(
        x => x.ExpansionType == null || x.ExpansionType == "" || x.ExpansionType.toUpperCase() != "HOUSEHOLD"
    );
    const noExpiratonDate = ExportPubmaticTemplateFields.filter(x => !x.ExpirationDate);
    const hasExpiratonDate = ExportPubmaticTemplateFields.filter(x => x.ExpirationDate);

    if (noAudienceName.length > 0) {
        errors.push("All Driver File Audience Name must be populated.");
    }

    if (noDescription.length > 0) {
        errors.push("All Driver File Description must be populated.");
    }

    if (noExpansionType.length > 0) {
        errors.push("All Driver File Expansion Type must be populated.");
    }

    if (noExpiratonDate.length > 0) {
        errors.push("All Driver File Expiration Dates must be populated.");
    }

    if (audienceNameLength.length > 0) {
        errors.push(
            "One or More Audience Names exceed 100 characters - please edit the Audience Name to be less than 100 characters."
        );
    }

    if (hasExpiratonDate.length > 0) {
        let invalidDate = false;
        const currDate = new Date();
        hasExpiratonDate.forEach(x => {
            const formateDate = new Date(x.ExpirationDate);
            if (formateDate < currDate) {
                invalidDate = true;
            }
        });

        if (invalidDate) {
            errors.push("Expiration dates may not be in the past. Check with sales on Contract Expiration Date.");
        }
    }

    if (DeliveryType.ThirdPartyCustom == deliveryType) {
        // Cost Per Million
        const anyCostPerMillion = ExportPubmaticTemplateFields.filter(
            x => x.CostPerMillion == 0 && x.RateType == "CPM"
        );
        if (anyCostPerMillion.length > 0) {
            errors.push("Driver File: If 'CPM rate type' is selected, audience must have a Cost Per Million value.");
        }


        // Hybrid
        const anyHybrid = ExportPubmaticTemplateFields.filter(
            x => (x.CostPerMillion == 0) && x.RateType == "Hybrid"
        );
        if (anyHybrid.length > 0) {
            errors.push("CPM cannot be zero.");
        }

        // Parent Element Id

        // const noProviderElementId = ExportPubmaticTemplateFields.filter(
        //     x => x.ProviderElementId == null || x.ProviderElementId == ""
        // );

        // if (noProviderElementId.length > 0) {
        //     errors.push("All Driver File Provider Element Id must be populated.");
        // }
    } else if (DeliveryType.ThirdPartySyndicated == deliveryType) {
        // const noProviderElementId = ExportPubmaticTemplateFields.filter(
        //     x => x.ProviderElementId == null || x.ProviderElementId == ""
        // );
        // if (noProviderElementId.length > 0) {
        //     errors.push("All Driver File Provider Element Id must be populated.");
        // }
    }

    return errors;
};
